/**
 * WebSocket Context Provider
 * Provides real-time communication capabilities throughout the app
 * Includes sound and browser push notifications
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { toast } from 'sonner';
import { 
  triggerNotification, 
  requestNotificationPermission, 
  supportsNotifications,
  getNotificationSettings,
  saveNotificationSettings 
} from '../utils/notifications';

const WebSocketContext = createContext(null);

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({}); // { conversationId: [userId, ...] }
  const [newMessages, setNewMessages] = useState([]); // Recent messages for active conversations
  const [notificationSettings, setNotificationSettings] = useState(getNotificationSettings());
  const [permissionStatus, setPermissionStatus] = useState(
    supportsNotifications() ? Notification.permission : 'denied'
  );

  // Request notification permission on mount
  useEffect(() => {
    if (supportsNotifications() && Notification.permission === 'default') {
      // Wait a bit before requesting to avoid being too aggressive
      const timer = setTimeout(async () => {
        const permission = await requestNotificationPermission();
        setPermissionStatus(permission);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle incoming notification
  const handleNotification = useCallback((notification) => {
    console.log('Real-time notification received:', notification);
    
    // Add to notifications list
    setNotifications(prev => [notification, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);
    
    // Show toast notification
    toast(notification.title, {
      description: notification.message,
      duration: 5000,
    });
    
    // Trigger sound and/or browser push notification
    const notificationType = notification.notification_type || notification.type || 'default';
    triggerNotification(
      notification.title,
      notification.message,
      notificationType,
      {
        data: notification.data,
        onClick: () => {
          // Handle notification click - could navigate to relevant page
          console.log('Notification clicked:', notification);
        }
      }
    );
  }, []);

  // Handle incoming chat message
  const handleChatMessage = useCallback((data) => {
    console.log('Real-time chat message received:', data);
    
    // Add to new messages
    setNewMessages(prev => [...prev, data]);
    
    // Clear typing indicator for this user/conversation
    if (data.message?.sender_id) {
      setTypingUsers(prev => {
        const conversationTyping = prev[data.conversation_id] || [];
        return {
          ...prev,
          [data.conversation_id]: conversationTyping.filter(id => id !== data.message.sender_id)
        };
      });
    }
    
    // Trigger notification for new message
    const senderName = data.message?.sender_name || 'Someone';
    const messagePreview = data.message?.message?.substring(0, 50) || 'New message';
    
    triggerNotification(
      `New message from ${senderName}`,
      messagePreview,
      data.message?.is_urgent ? 'urgent' : 'message',
      {
        tag: `chat-${data.conversation_id}`,
        data: { conversationId: data.conversation_id }
      }
    );
  }, []);

  // Handle typing indicator
  const handleTyping = useCallback((data) => {
    setTypingUsers(prev => {
      const conversationTyping = prev[data.conversation_id] || [];
      
      if (data.is_typing) {
        // Add user to typing list if not already there
        if (!conversationTyping.includes(data.user_id)) {
          return {
            ...prev,
            [data.conversation_id]: [...conversationTyping, data.user_id]
          };
        }
      } else {
        // Remove user from typing list
        return {
          ...prev,
          [data.conversation_id]: conversationTyping.filter(id => id !== data.user_id)
        };
      }
      return prev;
    });
  }, []);

  // Handle online status changes
  const handleOnlineStatus = useCallback((data) => {
    setOnlineUsers(prev => {
      const newSet = new Set(prev);
      if (data.status === 'online') {
        newSet.add(data.user_id);
      } else {
        newSet.delete(data.user_id);
      }
      return newSet;
    });
  }, []);

  // Initialize WebSocket
  const {
    isConnected,
    connectionError,
    sendChatMessage,
    sendTypingIndicator,
    markAsRead,
    reconnect
  } = useWebSocket(
    handleNotification,
    handleChatMessage,
    handleTyping,
    handleOnlineStatus
  );

  // Clear a specific message from newMessages (called after processing)
  const clearNewMessage = useCallback((messageId) => {
    setNewMessages(prev => prev.filter(m => m.message?.id !== messageId));
  }, []);

  // Clear all new messages for a conversation
  const clearNewMessagesForConversation = useCallback((conversationId) => {
    setNewMessages(prev => prev.filter(m => m.conversation_id !== conversationId));
  }, []);

  // Mark notifications as read
  const markNotificationsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Check if user is online
  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  // Get typing users for a conversation
  const getTypingUsers = useCallback((conversationId) => {
    return typingUsers[conversationId] || [];
  }, [typingUsers]);

  // Update notification settings
  const updateNotificationSettings = useCallback((newSettings) => {
    const updated = { ...notificationSettings, ...newSettings };
    setNotificationSettings(updated);
    saveNotificationSettings(updated);
  }, [notificationSettings]);

  // Request notification permission manually
  const requestPermission = useCallback(async () => {
    const permission = await requestNotificationPermission();
    setPermissionStatus(permission);
    return permission;
  }, []);

  const value = {
    // Connection state
    isConnected,
    connectionError,
    reconnect,
    
    // Notifications
    notifications,
    unreadCount,
    markNotificationsRead,
    
    // Chat
    sendChatMessage,
    sendTypingIndicator,
    markAsRead,
    newMessages,
    clearNewMessage,
    clearNewMessagesForConversation,
    
    // Online status
    onlineUsers: Array.from(onlineUsers),
    isUserOnline,
    
    // Typing
    typingUsers,
    getTypingUsers,
    
    // Notification settings
    notificationSettings,
    updateNotificationSettings,
    permissionStatus,
    requestPermission,
    supportsNotifications: supportsNotifications()
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
